import { BASE_URL } from '../env';
import { ApiErrorCode } from './apiErrorCodes';
import { useAppSessionStore } from '../stores/session/appSessionStore';
import { useSessionStore } from '../stores/session/sessionStore';
import { getCiu } from '../stores/tenantStore';

// Refresh the app access token shortly before it expires to avoid races at the boundary.
const APP_TOKEN_REFRESH_SKEW_MS = 60 * 1000;

// Single-flight guards: the app refresh token is single-use, so concurrent refreshes would each
// rotate it and invalidate the others. Portal and desktop use separate guards because they hit
// different endpoints — sharing a guard would cause a session-kind mismatch to burn the token
// against the wrong endpoint (the second caller would await the first one's wrong-endpoint Promise).
let portalRefreshInFlight: Promise<string | null> | null = null;
let desktopRefreshInFlight: Promise<DesktopRefreshResult | null> | null = null;

/**
 * Exchanges the stored app refresh token for a fresh access+refresh pair via
 * /connect/portal-refresh, updates the store, and returns the new access token.
 * Returns null when there is no refresh token / CIU, or the refresh is rejected (session expired).
 * Sends only the CIU header on purpose — it must not trigger the AcceoConnect token refresh,
 * which depends on the (possibly expired) host IS JWT.
 */
export async function refreshAppSession(): Promise<string | null> {
  if (portalRefreshInFlight) return portalRefreshInFlight;
  portalRefreshInFlight = (async () => {
    const { appRefreshToken } = useAppSessionStore.getState();
    const ciu = getCiu();
    if (!appRefreshToken || ciu === null) return null;
    try {
      const response = await fetch(`${BASE_URL}/connect/portal-refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-acceo-ciu': ciu },
        body: JSON.stringify({ refresh_token: appRefreshToken }),
      });
      if (!response.ok) return null;
      const data = await response.json() as {
        app_access_token: string; app_refresh_token: string; app_expires_in: number;
      };
      useAppSessionStore.getState().setAppSession(
        data.app_access_token, data.app_refresh_token, data.app_expires_in);
      return data.app_access_token;
    } catch {
      return null;
    }
  })().finally(() => { portalRefreshInFlight = null; });
  return portalRefreshInFlight;
}

/**
 * Mints the browser AcombaGO app session (the OIDC analogue of the portal nonce exchange) by presenting
 * the live OIDC access token to POST /connect/app-session for the selected company, then stores the
 * returned app tokens and marks the session kind 'browser'. Called at login, on a company switch, and
 * as a re-mint fallback in useAuthGuard. Uses a raw fetch with an explicit Bearer header — this runs
 * before any app token exists, so it must not go through apiClient (whose bearer is the app token we're
 * minting). Returns true on success.
 */
export async function establishBrowserAppSession(
  oidcAccessToken: string,
  ciu: string,
  tenantCode: string,
): Promise<boolean> {
  try {
    const response = await fetch(`${BASE_URL}/connect/app-session`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${oidcAccessToken}`,
        'x-acceo-ciu': ciu,
        'X-Tenant-Code': tenantCode,
      },
    });
    if (!response.ok) return false;
    const data = await response.json() as {
      app_access_token: string; app_refresh_token: string; app_expires_in: number;
    };
    // Ordering matters: setAppSession first, setSessionKind second. These are separate stores,
    // so they fire two independent React notifications with a sub-millisecond window in between.
    // Code that reacts to appRefreshToken must tolerate sessionKind being null at that instant.
    // The re-entry guard in useAuthGuard prevents double-execution during that window.
    useAppSessionStore.getState().setAppSession(data.app_access_token, data.app_refresh_token, data.app_expires_in);
    useSessionStore.getState().setSessionKind('browser');
    return true;
  } catch {
    return false;
  }
}

export interface DesktopAuthResult {
  app_access_token: string;
  app_refresh_token: string;
  expires_in: number;
  workspaceCiu: string;
  companyName: string;
  isStaleCopy: boolean;
}

// Why the assertion was rejected. 'no-company-access' maps a 401/tenantInvalidIdentity (the société in
// the assertion was not recognized) to the specific SigninError message; 'generic' covers everything
// else (network failure, 5xx, unexpected shapes) and shows the generic error.
export type DesktopAuthFailureReason = 'no-company-access' | 'generic';

export type DesktopAuthOutcome =
  | { ok: true; result: DesktopAuthResult }
  | { ok: false; reason: DesktopAuthFailureReason };

async function readDesktopAuthFailureReason(response: Response): Promise<DesktopAuthFailureReason> {
  // Only a 401 carrying tenantInvalidIdentity means "company not recognized"; other statuses are transient/unknown.
  if (response.status !== 401) return 'generic';
  try {
    const body = await response.json() as { code?: string };
    return body.code === ApiErrorCode.TenantInvalidIdentity ? 'no-company-access' : 'generic';
  } catch {
    return 'generic';
  }
}

/**
 * Mints the Acomba Desktop session via POST /connect/desktop-auth. Presents the OIDC access token
 * (Identity Server JWT) and the assertion (opaque base64url token carrying ICIdentifiantPublique +
 * licenseCIU + highWaterMark). The backend resolves the Catalog workspace, provisions the tenant DB,
 * and returns our app tokens + workspace context. On success, stores the app tokens and marks the
 * session kind 'desktop'. Raw fetch with an explicit Bearer — this runs before any app token exists,
 * so it must not go through apiClient. On failure returns an outcome carrying a reason so the caller
 * can show the specific "no company access" message rather than a bare generic error.
 */
export async function establishDesktopAuthSession(
  oidcAccessToken: string,
  assertion: string,
): Promise<DesktopAuthOutcome> {
  try {
    const response = await fetch(`${BASE_URL}/connect/desktop-auth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${oidcAccessToken}` },
      body: JSON.stringify({ assertion }),
    });
    if (!response.ok) return { ok: false, reason: await readDesktopAuthFailureReason(response) };
    const data = await response.json() as DesktopAuthResult;
    // Same two-store write order as establishBrowserAppSession: tokens then session kind.
    useAppSessionStore.getState().setAppSession(data.app_access_token, data.app_refresh_token, data.expires_in);
    useSessionStore.getState().setSessionKind('desktop');
    return { ok: true, result: data };
  } catch {
    return { ok: false, reason: 'generic' };
  }
}

export interface DesktopRefreshResult {
  accessToken: string;
  isStaleCopy: boolean;
}

/**
 * Refreshes the desktop app session via POST /connect/desktop-refresh. Pass the high-water mark
 * decoded client-side from the current launch's `src` token (see utils/desktopLaunchToken.ts) so the
 * backend can freshly recompute `isStaleCopy` for the caller (the silent-reuse fast path in
 * DesktopAuth.tsx needs this to keep tenantStore.isStaleCopy accurate without an interactive
 * desktop-auth round trip). Omit it for a plain page-reload refresh (useAuthGuard), which has no
 * launch token to decode — the backend then reports isStaleCopy: false rather than guessing.
 * Returns the new access token + isStaleCopy on success, null on failure.
 */
export async function refreshDesktopAppSession(highWaterMark?: number | null): Promise<DesktopRefreshResult | null> {
  if (desktopRefreshInFlight) return desktopRefreshInFlight;
  desktopRefreshInFlight = (async () => {
    const { appRefreshToken } = useAppSessionStore.getState();
    const ciu = getCiu();
    if (!appRefreshToken || ciu === null) return null;
    try {
      const response = await fetch(`${BASE_URL}/connect/desktop-refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-acceo-ciu': ciu },
        body: JSON.stringify({ refresh_token: appRefreshToken, highWaterMark: highWaterMark ?? null }),
      });
      if (!response.ok) return null;
      const data = await response.json() as {
        app_access_token: string; app_refresh_token: string; app_expires_in: number; isStaleCopy: boolean;
      };
      useAppSessionStore.getState().setAppSession(
        data.app_access_token, data.app_refresh_token, data.app_expires_in);
      return { accessToken: data.app_access_token, isStaleCopy: data.isStaleCopy };
    } catch {
      return null;
    }
  })().finally(() => { desktopRefreshInFlight = null; });
  return desktopRefreshInFlight;
}

/**
 * Revokes the app session's refresh token via POST /connect/app-session/logout — called from
 * Header.tsx's handleLogoutConfirm before signoutRedirect, so the server-side session dies even when
 * Identity Server's own round trip never completes (its signed-out page can dead-end with no redirect
 * link to follow). Best-effort: swallows every failure, exactly like signoutRedirectCallback()/
 * removeUser() in SignoutCallback.tsx — a failed revocation must never block or fail the sign-out the
 * user already asked for. Does not touch any store; clearing session state stays SignoutCallback's job.
 */
export async function revokeAppSession(): Promise<void> {
  const { appRefreshToken } = useAppSessionStore.getState();
  const ciu = getCiu();
  if (!appRefreshToken || ciu === null) return;
  try {
    await fetch(`${BASE_URL}/connect/app-session/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-acceo-ciu': ciu },
      body: JSON.stringify({ refresh_token: appRefreshToken }),
    });
  } catch {
    // Best-effort — see function doc.
  }
}

/** Returns a valid app access token, refreshing it first if it is missing or near expiry. */
export async function ensureFreshAppToken(): Promise<string | null> {
  const { appAccessToken, appAccessExpiresAt, appRefreshToken } = useAppSessionStore.getState();
  if (!appRefreshToken) return null;
  const stillFresh = appAccessToken !== null && appAccessExpiresAt !== null
    && Date.now() < appAccessExpiresAt - APP_TOKEN_REFRESH_SKEW_MS;
  if (stillFresh) return appAccessToken;
  return refreshAppSession();
}
